// One rule for every win-rate display: a rate needs a sample behind it. With
// nothing closed "0.0%" reads as a real, terrible score, and a single lucky
// close reads as "100%". Show "—" until MIN_WIN_RATE_SAMPLE outcomes exist.

export const NO_CLOSED_SIGNALS = 'no closed signals yet'
export const NO_CLOSED_TRADES = 'no closed trades yet'

/** Fewest closed signals/trades a win rate is shown from (backend: MIN_WIN_RATE_SAMPLE). */
export const MIN_WIN_RATE_SAMPLE = 5

/**
 * Closed signals in the record before the daily trend views (Win Rate Trend,
 * its daily table, Win Rate by Regime) are shown. Daily and per-regime slices
 * of a smaller record are a handful of trades each: noise drawn as a trend.
 */
export const TREND_MIN_CLOSED = 200

/**
 * Whether a win rate is worth showing. False when the rate is missing, or when
 * the caller knows the closed count and it is below MIN_WIN_RATE_SAMPLE (older
 * endpoints send 0 rather than null with nothing closed). Callers that have no
 * count (null/undefined) get the rate as-is.
 */
export const hasWinRate = (
  rate: number | null | undefined,
  closedCount?: number | null
): rate is number =>
  rate != null &&
  Number.isFinite(rate) &&
  (closedCount == null || closedCount >= MIN_WIN_RATE_SAMPLE)

/** "62.5%", or "—" when there is no rate or too small a sample to quote one. */
export const formatWinRate = (rate: number | null | undefined, closedCount?: number | null): string =>
  hasWinRate(rate, closedCount) ? `${rate.toFixed(1)}%` : '—'

/**
 * The note under a "—" win rate: "no closed trades yet" with nothing closed,
 * otherwise how far the sample is from MIN_WIN_RATE_SAMPLE ("2 closed · 3 more needed").
 */
export const pendingWinRateNote = (
  closedCount: number | null | undefined,
  kind: 'signal' | 'trade'
): string => {
  const closed = closedCount ?? 0
  if (closed <= 0) return kind === 'signal' ? NO_CLOSED_SIGNALS : NO_CLOSED_TRADES
  return `${closed} closed · ${Math.max(MIN_WIN_RATE_SAMPLE - closed, 0)} more needed`
}

/**
 * Below this many closes a win rate IS quoted (it has cleared
 * MIN_WIN_RATE_SAMPLE) but is marked as a small sample: at n = 10 a 95%
 * interval on a rate is still roughly ±30 points wide, so the percentage alone
 * reads far more settled than it is. Display only; nothing is computed from it.
 */
export const SMALL_SAMPLE_SIZE = 30

/** A rate that is shown, but on too few closes to lean on. */
export const isSmallSample = (closedCount: number | null | undefined): boolean => {
  const closed = closedCount ?? 0
  return closed >= MIN_WIN_RATE_SAMPLE && closed < SMALL_SAMPLE_SIZE
}

/** "56.3% (45/80)" -- the rate never travels without its denominator. "— (2/3)" below the minimum. */
export const formatWinRateWithCount = (
  rate: number | null | undefined,
  wins: number,
  closedCount: number
): string => `${formatWinRate(rate, closedCount)} (${wins}/${closedCount})`
